import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { MonitoringMetricResult } from '../../../../../types/DeployerControlMonitoring.types';
import { MetricStateBadge } from './MetricStateBadge';

interface MetricResultsAccordionProps {
  results: MonitoringMetricResult[];
}

interface CategoryGroup {
  category: string;
  categoryLabel: string;
  articleReference: string | null;
  results: MonitoringMetricResult[];
}

function groupByCategory(results: MonitoringMetricResult[]): CategoryGroup[] {
  const groups = new Map<string, CategoryGroup>();

  for (const result of results) {
    const existing = groups.get(result.category);
    if (existing) {
      existing.results.push(result);
    } else {
      groups.set(result.category, {
        category: result.category,
        categoryLabel: result.categoryLabel,
        articleReference: result.articleReference,
        results: [result]
      });
    }
  }

  return Array.from(groups.values());
}

export function MetricResultsAccordion({ results }: MetricResultsAccordionProps) {
  const groups = groupByCategory(results);

  return (
    <div className="flex flex-col gap-2">
      {groups.map((group) => (
        <Disclosure key={group.category} defaultOpen={false}>
          {({ open }) => (
            <div className="border border-gray-200 rounded-lg">
              <DisclosureButton className="w-full flex items-center justify-between px-4 py-3 text-left">
                <span className="font-medium text-sm text-gray-900">
                  {group.categoryLabel}
                  {group.articleReference ? (
                    <span className="text-gray-500 font-normal ml-2">
                      Article {group.articleReference}
                    </span>
                  ) : null}
                </span>
                <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
              </DisclosureButton>
              <DisclosurePanel className="px-4 pb-4 flex flex-col gap-3">
                {group.results.map((result) => (
                  <div key={result.id} className="flex flex-col gap-1 border-t border-gray-100 pt-3 first:border-t-0 first:pt-0">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-800 text-left">{result.metricDescription}</span>
                      <MetricStateBadge state={result.state} />
                    </div>
                    {result.explanation && (
                      <p className="text-xs text-gray-500 text-left">{result.explanation}</p>
                    )}
                  </div>
                ))}
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>
      ))}
    </div>
  );
}
